import type { EmployeeRole } from '~/models/EmployeeRoles/Roles.enum'
import type { Capability, PageName } from '../config'
import { CAPABILITY_CONFIG } from '../config'
import type { EmployeePermission } from '~/models/EmployeeRoles/Permissions.enum'

export class InvalidConfigException extends Error {}

export type CapabilityChecker = (capability: Capability) => boolean

export default function useRights() {
  const { permissions, userRoles } = useUser()

  const hasAdminRole = computed(() => userRoles.value.includes('Admin'))

  /**
   * Check if the current user can access a certain page.
   */
  function canAccessPage(pageName: PageName): boolean {
    const pageCapability = CAPABILITY_CONFIG.get(pageName)
    if (pageCapability === undefined) {
      return false
    }
    return hasCapability(pageCapability)
  }

  /*
   * Check if the current user has the required capability.
   */
  function hasCapability(capability: Capability): boolean {
    return capability.every(permission =>
      permissions.value.includes(permission),
    )
  }

  /*
   * Check if the current user has the required capability.
   */
  function hasRole(checkRole: EmployeeRole): boolean {
    // we exclude tech because some admins are not technical people.
    if (hasAdminRole.value && checkRole !== 'Tech') return true

    return userRoles.value.includes(checkRole)
  }

  /*
   * Check if the current user has the required capability.
   */
  function hasPermission(permission: EmployeePermission): boolean {
    return permissions.value.includes(permission)
  }

  return {
    hasRole,
    hasPermission,
    hasCapability,
    canAccessPage,
  }
}
